import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { caseStudies } from '../data/caseStudiesData';
import { ArrowLeft, Clock, Users, Briefcase, Building2, CheckCircle2 } from 'lucide-react';

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const caseStudy = caseStudies.find(cs => cs.slug === slug);

  if (!caseStudy) {
    return <Navigate to="/case-studies" replace />;
  }

  const { title, details, image, category } = caseStudy;
  const hasRichContent = details?.challenge || details?.approach || details?.keyFeatures || details?.metrics;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={title}
        description={details?.overview || caseStudy.description || `Case study: ${title}. See how Clik Technologies delivered results.`}
        path={`/case-studies/${slug}`}
        type="article"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-28 pb-12 overflow-hidden" style={{ background: '#F8FAFC' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px]" style={{ background: 'radial-gradient(circle, rgba(38,147,255,0.06) 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-[#2693FF] transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Case Studies</span>
          </Link>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-6">
            {category?.map((cat, idx) => (
              <span
                key={idx}
                className="text-xs px-3 py-1 rounded-full font-medium"
                style={{ background: 'rgba(38,147,255,0.08)', color: '#2693FF' }}
              >
                {cat}
              </span>
            ))}
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 font-heading mb-8 leading-tight">
            {title}
          </h1>

          {/* Quick Info Bar */}
          {hasRichContent && (
            <div className="flex flex-wrap gap-6 mb-8">
              {details?.client && (
                <div className="flex items-center gap-2 text-slate-500">
                  <Building2 className="w-4 h-4" style={{ color: '#2693FF' }} />
                  <span className="text-sm">{details.client}</span>
                </div>
              )}
              {details?.industry && (
                <div className="flex items-center gap-2 text-slate-500">
                  <Briefcase className="w-4 h-4" style={{ color: '#2693FF' }} />
                  <span className="text-sm">{details.industry}</span>
                </div>
              )}
              {details?.timeline && (
                <div className="flex items-center gap-2 text-slate-500">
                  <Clock className="w-4 h-4" style={{ color: '#2693FF' }} />
                  <span className="text-sm">{details.timeline}</span>
                </div>
              )}
              {details?.team && (
                <div className="flex items-center gap-2 text-slate-500">
                  <Users className="w-4 h-4" style={{ color: '#2693FF' }} />
                  <span className="text-sm">{details.team}</span>
                </div>
              )}
            </div>
          )}

          {image && (
            <div className="relative h-64 sm:h-80 md:h-[420px] overflow-hidden rounded-2xl border border-slate-100" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <img
                src={image}
                alt={title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
      </section>

      {/* Metrics Bar */}
      {details?.metrics && details.metrics.length > 0 && (
        <section className="bg-white border-b border-slate-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {details.metrics.map((metric, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-xl sm:text-2xl font-bold font-heading mb-1" style={{ color: '#2693FF' }}>
                    {metric.value}
                  </div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Overview */}
          {details?.overview && (
            <div className="mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-6">Overview</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {details.overview}
              </p>
            </div>
          )}

          {/* Challenge */}
          {details?.challenge && (
            <div className="mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-6">The Challenge</h2>
              <div className="rounded-2xl p-6 sm:p-8 border border-slate-100" style={{ background: '#F8FAFC', borderLeft: '4px solid #2693FF' }}>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {details.challenge}
                </p>
              </div>
            </div>
          )}

          {/* Approach */}
          {details?.approach && details.approach.length > 0 && (
            <div className="mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-8">Our Approach</h2>
              <div className="space-y-6">
                {details.approach.map((step, idx) => (
                  <div key={idx} className="flex gap-5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm mt-1" style={{ background: '#2693FF' }}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900 font-heading mb-2">{step.title}</h3>
                      <p className="text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Solution */}
          {details?.solution && (
            <div className="mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-6">The Solution</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {details.solution}
              </p>
            </div>
          )}

          {/* Key Features */}
          {details?.keyFeatures && details.keyFeatures.length > 0 && (
            <div className="mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-6">Key Features</h2>
              <div className="grid grid-cols-1 gap-4">
                {details.keyFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 rounded-xl border border-slate-100" style={{ background: '#F8FAFC' }}>
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2693FF' }} />
                    <p className="text-slate-600">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Technologies */}
          {details?.technologies && details.technologies.length > 0 && (
            <div className="mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-6">Technologies Used</h2>
              <div className="flex flex-wrap gap-3">
                {details.technologies.map((tech, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 rounded-full text-sm font-medium"
                    style={{ background: 'rgba(38,147,255,0.08)', color: '#2693FF' }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Engagement Details */}
          {hasRichContent && (details?.engagement || details?.team || details?.timeline) && (
            <div className="mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-6">Engagement Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {details?.engagement && (
                  <div className="rounded-2xl p-5 text-center border border-slate-100" style={{ background: '#F8FAFC' }}>
                    <Briefcase className="w-6 h-6 mx-auto mb-2" style={{ color: '#2693FF' }} />
                    <div className="text-sm text-slate-400 mb-1">Model</div>
                    <div className="font-semibold text-slate-900 font-heading">{details.engagement}</div>
                  </div>
                )}
                {details?.team && (
                  <div className="rounded-2xl p-5 text-center border border-slate-100" style={{ background: '#F8FAFC' }}>
                    <Users className="w-6 h-6 mx-auto mb-2" style={{ color: '#2693FF' }} />
                    <div className="text-sm text-slate-400 mb-1">Team</div>
                    <div className="font-semibold text-slate-900 font-heading">{details.team}</div>
                  </div>
                )}
                {details?.timeline && (
                  <div className="rounded-2xl p-5 text-center border border-slate-100" style={{ background: '#F8FAFC' }}>
                    <Clock className="w-6 h-6 mx-auto mb-2" style={{ color: '#2693FF' }} />
                    <div className="text-sm text-slate-400 mb-1">Timeline</div>
                    <div className="font-semibold text-slate-900 font-heading">{details.timeline}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Outcome */}
          {details?.outcome && (
            <div className="mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-6">Results & Impact</h2>
              <div className="rounded-2xl p-6 sm:p-8 border border-slate-100" style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, rgba(38,147,255,0.04) 100%)' }}>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {details.outcome}
                </p>
              </div>
            </div>
          )}

          {/* Simple fallback for non-rich case studies */}
          {!hasRichContent && (
            <div className="space-y-6 mb-14">
              {details?.team && (
                <div className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ background: '#2693FF' }} />
                  <p className="text-lg text-slate-700">
                    <span className="font-semibold font-heading">Team:</span> {details.team}
                  </p>
                </div>
              )}
              {details?.technologies && details.technologies.length > 0 && (
                <div className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ background: '#2693FF' }} />
                  <p className="text-lg text-slate-700">
                    <span className="font-semibold font-heading">Technologies:</span> {details.technologies.join(', ')}
                  </p>
                </div>
              )}
              {details?.outcome && (
                <div className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ background: '#2693FF' }} />
                  <p className="text-lg text-slate-700">
                    <span className="font-semibold font-heading">Outcome:</span> {details.outcome}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* CTA */}
          <div className="border-t border-slate-100 pt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500">
              Interested in a similar solution for your business?
            </p>
            <Link
              to="/services"
              className="btn-gradient inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium"
            >
              Explore Our Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudyDetail;
